import { useState, useEffect, useCallback } from 'react';
import pageBuilderService from '../services/pageBuilderService';
import { PageSection, PageName, UpdateSectionRequest } from '../types/pageBuilder';

interface UsePageSectionsOptions {
  pageName: PageName;
  autoFetch?: boolean;
}

interface UsePageSectionsReturn {
  sections: PageSection[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateSection: (sectionId: number, data: UpdateSectionRequest) => Promise<void>;
  deleteSection: (sectionId: number) => Promise<void>;
  reorderSections: (newOrder: { id: number; display_order: number }[]) => Promise<void>;
  toggleVisibility: (sectionId: number, isVisible: boolean) => Promise<void>;
}

/**
 * Custom hook to fetch and manage page sections
 * @param options - Configuration options
 * @returns Sections, loading state, error, and management functions
 */
export const usePageSections = ({
  pageName,
  autoFetch = true
}: UsePageSectionsOptions): UsePageSectionsReturn => {
  const [sections, setSections] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSections = useCallback(async () => {
    if (!pageName) return;

    setLoading(true);
    setError(null);

    try {
      const data = await pageBuilderService.getSections(pageName);
      // Sort by display_order
      const sortedSections = data.sort((a, b) => a.display_order - b.display_order);
      setSections(sortedSections);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load sections';
      setError(errorMessage);    } finally {
      setLoading(false);
    }
  }, [pageName]);

  useEffect(() => {
    if (autoFetch) {
      fetchSections();
    }
  }, [autoFetch, fetchSections]);

  const updateSection = useCallback(
    async (sectionId: number, data: UpdateSectionRequest) => {
      try {
        await pageBuilderService.updateSection(sectionId, data);
        await fetchSections(); // Refresh sections after update
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update section';
        setError(errorMessage);
        throw err;
      }
    },
    [fetchSections]
  );

  const deleteSection = useCallback(
    async (sectionId: number) => {
      try {
        await pageBuilderService.deleteSection(sectionId);
        await fetchSections(); // Refresh sections after delete
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete section';
        setError(errorMessage);
        throw err;
      }
    },
    [fetchSections]
  );

  const reorderSections = useCallback(
    async (newOrder: { id: number; display_order: number }[]) => {
      try {
        await pageBuilderService.reorderSections({
          page_name: pageName,
          sections: newOrder
        });
        await fetchSections(); // Refresh sections after reorder
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to reorder sections';
        setError(errorMessage);
        throw err;
      }
    },
    [pageName, fetchSections]
  );

  const toggleVisibility = useCallback(
    async (sectionId: number, isVisible: boolean) => {
      try {
        await pageBuilderService.toggleSectionVisibility(sectionId, isVisible);
        await fetchSections(); // Refresh sections after toggle
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to toggle visibility';
        setError(errorMessage);
        throw err;
      }
    },
    [fetchSections]
  );

  return {
    sections,
    loading,
    error,
    refetch: fetchSections,
    updateSection,
    deleteSection,
    reorderSections,
    toggleVisibility
  };
};

export default usePageSections;
